import React, { useState, useRef } from 'react';
import { Note, UserProfile } from '../types';
import { X, MessageSquare, Send, User, AtSign, Eye } from 'lucide-react';
import { Button } from './Button';
import { READ_ONLY_AUSWEG, READ_ONLY_HINWEIS } from '../services/readOnlyNotice';

interface Props {
  notes: Note[];
  onClose: () => void;
  onAddNote: (text: string, mentions?: string[]) => void;
  currentUser?: any;
  availableUsers?: UserProfile[];
  /**
   * Schreibgeschuetzt geoeffnetes Projekt (Admin → Offene Positionen).
   * Der Riegel in ProjectStats.onAddNote bleibt als zweite Reihe stehen; ohne
   * diese Prop hier bliebe das Eingabefeld sichtbar und wuerde sich beim
   * Absenden leeren, obwohl nichts gespeichert wird. Lesen bleibt erlaubt.
   */
  isReadOnly?: boolean;
}

/**
 * Extract mentioned user IDs from text by matching @FullName against available users.
 * Uses exact full-name matching to prevent partial matches (e.g. "Ehlers" matching all Ehlers).
 */
const extractMentionedIds = (text: string, users: UserProfile[]): string[] => {
    const ids = new Set<string>();
    // Sort users by name length descending so "Mareike Ehlers" is matched before "Ehlers"
    const sortedUsers = [...users].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.length;
        const nameB = `${b.first_name} ${b.last_name}`.length;
        return nameB - nameA;
    });

    let remaining = text;
    for (const user of sortedUsers) {
        const fullName = `${user.first_name} ${user.last_name}`;
        // Match @FullName (case-insensitive)
        const regex = new RegExp(`@${fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
        if (regex.test(remaining)) {
            ids.add(user.id);
            // Remove matched mention to prevent substring re-matching
            remaining = remaining.replace(regex, '');
        }
    }
    return Array.from(ids);
};

export const NotesModal: React.FC<Props> = ({ notes, onClose, onAddNote, availableUsers = [], isReadOnly = false }) => {
  const [newNote, setNewNote] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    if (!newNote.trim()) return;
    // Extract mentions from final text — single source of truth
    const mentionedIds = extractMentionedIds(newNote, availableUsers);
    onAddNote(newNote, mentionedIds);
    setNewNote('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setNewNote(val);

    const words = val.split(' ');
    const lastWord = words[words.length - 1];

    if (lastWord.startsWith('@') && availableUsers.length > 0) {
      setShowMentions(true);
      setMentionQuery(lastWord.slice(1).toLowerCase());
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (user: UserProfile) => {
    const words = newNote.split(' ');
    words.pop();
    const name = `${user.first_name} ${user.last_name}`;
    const text = [...words, `@${name} `].join(' ');
    setNewNote(text);
    // No need for mentionedIds Set — we extract from text on submit
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredUsers = availableUsers.filter(u => {
    const fullName = `${u.first_name} ${u.last_name}`.toLowerCase();
    const firstName = (u.first_name || '').toLowerCase();
    const lastName = (u.last_name || '').toLowerCase();
    const q = mentionQuery.toLowerCase();
    // Match from start of first name, last name, or full name — not substring
    return firstName.startsWith(q) || lastName.startsWith(q) || fullName.startsWith(q);
  });

  return (
    <div data-glass-view="projekt-dialog" className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-scale-in relative">
        
        <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
            <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-indigo-400" />
                    Mitteilungszentrale
                </h3>
                <p className="text-slate-400 text-xs mt-1">Interne Notizen, Updates & Verlauf</p>
            </div>
            <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-colors"><X className="w-5 h-5"/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 flex flex-col gap-3" onClick={() => setShowMentions(false)}>
            {notes.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 italic py-10">
                    <MessageSquare className="w-12 h-12 mb-2 opacity-20" />
                    <p>Keine Nachrichten vorhanden.</p>
                </div>
            ) : (
                notes.map((note) => (
                    <div key={note.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex justify-between text-xs text-slate-400 mb-2 border-b border-slate-50 pb-2">
                            <span className="font-bold text-slate-700 flex items-center gap-1">
                                <User className="w-3 h-3 text-blue-500" /> 
                                {note.author}
                            </span>
                            <span>{new Date(note.date).toLocaleString('de-DE')}</span>
                        </div>
                        <p className="text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">{note.text}</p>
                    </div>
                ))
            )}
        </div>

        <div className="p-4 bg-white border-t border-slate-200 shrink-0 relative">
            {isReadOnly ? (
                <div className="flex justify-between items-center gap-4">
                    <p className="text-[11px] text-slate-500 leading-relaxed flex items-start gap-2">
                        <Eye className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                        <span>{READ_ONLY_HINWEIS} Neue Notizen sind hier nicht möglich. {READ_ONLY_AUSWEG}</span>
                    </p>
                    <Button variant="ghost" onClick={onClose} className="text-xs shrink-0">Schließen</Button>
                </div>
            ) : (
            <>
            {showMentions && filteredUsers.length > 0 && (
                <div className="absolute bottom-full left-4 mb-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 animate-in slide-in-from-bottom-2 duration-200">
                    <div className="bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-500 uppercase border-b border-slate-100">Erwähnen...</div>
                    <div className="max-h-48 overflow-y-auto">
                        {filteredUsers.map(u => (
                            <div 
                                key={u.id} 
                                onClick={() => handleMentionSelect(u)}
                                className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm flex items-center justify-between border-b border-slate-50 last:border-0 group transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">
                                        {u.first_name?.[0]}{u.last_name?.[0]}
                                    </div>
                                    <span className="font-bold text-slate-700">{u.first_name} {u.last_name}</span>
                                </div>
                                <AtSign className="w-3 h-3 text-slate-300 group-hover:text-blue-500" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex gap-2">
               <div className="relative flex-1">
                   <input 
                       ref={inputRef}
                       type="text" 
                       value={newNote} 
                       onChange={handleInputChange}
                       onKeyDown={(e) => e.key === 'Enter' && !showMentions && handleSubmit()}
                       placeholder="Neue Notiz verfassen (nutzen Sie @ für Erwähnungen)..."
                       className="w-full border border-slate-300 rounded-lg px-4 py-3 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm"
                       autoFocus
                   />
               </div>
               <button
                   onClick={handleSubmit}
                   disabled={!newNote.trim()}
                   className="bg-blue-600 text-white px-5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
               >
                   <Send className="w-5 h-5" />
               </button>
            </div>
            <div className="flex justify-between mt-2 items-center">
                 <span className="text-[11px] text-slate-400 flex items-center gap-1">
                    <AtSign className="w-3 h-3" /> Tipp: Mit @ Kollegen erwähnen
                 </span>
                 <Button variant="ghost" onClick={onClose} className="text-xs">Schließen</Button>
            </div>
            </>
            )}
        </div>
       </div>
    </div>
  );
};
